// frontend/src/pages/Settings/Settings.js
import React, { useEffect, useState, lazy, Suspense } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GroupIcon from '@mui/icons-material/Group';
import { getCurrentUser, getCurrentUserName, getUserRole } from '../../utils/auth';

const SettingsUserManagement = lazy(() => import('./SettingsUserManagement'));

const SETTINGS_KEY = 'erp_app_settings';

const defaultSettings = {
  companyName: 'Sun Systems International',
  currency: 'LKR',
  taxRate: 0,
  lowStockThreshold: 10,
  invoicePrefix: 'INV',
  quotationPrefix: 'QUOT',
  rentalPrefix: 'RENT',
  notifications: {
    email: true,
    push: false,
    lowStock: true,
    overdue: true
  }
};

function Settings() {
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState(defaultSettings);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const user = getCurrentUser();
  const displayName = getCurrentUserName();
  const role = getUserRole();

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) {
        setSettings({ ...defaultSettings, ...JSON.parse(saved) });
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleNotificationChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };

  const saveSettings = () => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      setSuccess('Settings saved on this device');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Could not save settings');
    }
  };

  const resetToDefault = () => {
    if (!window.confirm('Reset all settings to default?')) return;
    setSettings(defaultSettings);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(defaultSettings));
    setSuccess('Settings reset to default');
    setTimeout(() => setSuccess(''), 3000);
  };

  const tabs = [
    { label: 'General', icon: <TuneIcon fontSize="small" /> },
    { label: 'Company', icon: <BusinessIcon fontSize="small" /> },
    { label: 'Notifications', icon: <NotificationsIcon fontSize="small" /> },
    { label: 'Users', icon: <GroupIcon fontSize="small" /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Settings
        </Typography>
        <Typography color="text.secondary">
          Company defaults, notifications, and user management
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f8fafc' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Avatar sx={{ bgcolor: '#1565c0', width: 56, height: 56 }}>
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h6" fontWeight={600}>{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email || '—'} · ID: {user.id || '—'}
            </Typography>
          </Box>
          <Chip label={(role || 'staff').replace(/_/g, ' ').toUpperCase()} color="primary" variant="outlined" />
          <Chip label="Active" color="success" size="small" />
        </CardContent>
      </Card>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1, bgcolor: '#fff' }}
        >
          {tabs.map((t, i) => (
            <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} value={i} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>General</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Defaults used across POS, invoices, and stock alerts
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Default currency"
                  value={settings.currency}
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                >
                  <MenuItem value="LKR">Sri Lankan Rupee (LKR)</MenuItem>
                  <MenuItem value="USD">US Dollar (USD)</MenuItem>
                  <MenuItem value="EUR">Euro (EUR)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Default tax rate (%)"
                  value={settings.taxRate}
                  onChange={(e) => handleSettingChange('taxRate', e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Low stock threshold"
                  value={settings.lowStockThreshold}
                  onChange={(e) => handleSettingChange('lowStockThreshold', e.target.value)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Company information</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company name"
                  value={settings.companyName}
                  onChange={(e) => handleSettingChange('companyName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Invoice prefix"
                  value={settings.invoicePrefix}
                  onChange={(e) => handleSettingChange('invoicePrefix', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Quotation prefix"
                  value={settings.quotationPrefix}
                  onChange={(e) => handleSettingChange('quotationPrefix', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Rental prefix"
                  value={settings.rentalPrefix}
                  onChange={(e) => handleSettingChange('rentalPrefix', e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>Notifications</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Preferences are saved on this browser (server push not required)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.notifications.email}
                      onChange={(e) => handleNotificationChange('email', e.target.checked)}
                    />
                  }
                  label="Email notifications"
                />
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.notifications.push}
                      onChange={(e) => handleNotificationChange('push', e.target.checked)}
                    />
                  }
                  label="Browser push notifications"
                />
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.notifications.lowStock}
                      onChange={(e) => handleNotificationChange('lowStock', e.target.checked)}
                    />
                  }
                  label="Low stock alerts"
                />
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.notifications.overdue}
                      onChange={(e) => handleNotificationChange('overdue', e.target.checked)}
                    />
                  }
                  label="Overdue account alerts"
                />
              </Paper>
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>User management</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create and manage ERP users. Document prints use each user&apos;s full name (not role).
              </Typography>
              <Suspense fallback={<Typography>Loading users…</Typography>}>
                <SettingsUserManagement />
              </Suspense>
            </Box>
          )}

          {activeTab !== 3 && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveSettings}>
                Save settings
              </Button>
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetToDefault}>
                Reset defaults
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default Settings;
